/**
 * Audience-gate semantics shared by the client (which reads a Remote Config
 * string) and the backend (which reads the same shape out of a Firestore
 * control document). One implementation so the two sides cannot drift on what
 * "enabled for this company" means.
 *
 * Allowed shapes:
 *   - boolean, or the strings "true" / "false"
 *   - audience object: { enabled: boolean, allowlist?: string[] }
 *
 * Fails closed: anything empty, missing or malformed resolves to false, so a
 * broken value never exposes a gated feature.
 */
export function evaluateAudienceValue(
  value: unknown,
  isAllowlisted: (allowlist: string[]) => boolean
): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value !== 'object' || value === null) return false

  const audience = value as { enabled?: unknown; allowlist?: unknown }
  if (typeof audience.enabled !== 'boolean') return false
  if (audience.enabled === true) return true
  return Array.isArray(audience.allowlist) && isAllowlisted(audience.allowlist)
}

/** `evaluateAudienceValue` for a value that arrives as a Remote Config string. */
export function evaluateAudienceFlag(
  raw: string | null | undefined,
  isAllowlisted: (allowlist: string[]) => boolean
): boolean {
  if (typeof raw !== 'string') return false
  const trimmed = raw.trim()
  if (trimmed === '') return false
  if (trimmed === 'true') return true
  if (trimmed === 'false') return false

  let parsed: unknown
  try {
    parsed = JSON.parse(trimmed)
  } catch {
    return false
  }
  return evaluateAudienceValue(parsed, isAllowlisted)
}

export function parseAudienceFlag(
  raw: string | null | undefined,
  uid: string | null
): boolean {
  return evaluateAudienceFlag(
    raw,
    (allowlist) => uid !== null && allowlist.includes(uid)
  )
}

export function parseCompanyAudienceFlag(
  raw: string | null | undefined,
  companyIds: readonly string[]
): boolean {
  return evaluateAudienceFlag(raw, (allowlist) =>
    companyIds.some((id) => allowlist.includes(id))
  )
}

/** Company-audience decision for an already-parsed value (the backend path). */
export function isCompanyInAudience(
  value: unknown,
  companyUid: string
): boolean {
  return evaluateAudienceValue(value, (allowlist) =>
    allowlist.includes(companyUid)
  )
}
